import React, { useState } from 'react';
import { Mail, Phone, MapPin, MessageSquare, Send, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { useEmailService, type ContactFormData } from '../hooks/useEmailSender';
import { FacebookIcon, InstagramIcon } from './Icons';
import { useTranslation } from '../hooks/useTranslation';
import { trackEvent } from '../utils/analytics';

export default function ContactUs() {
	const { t } = useTranslation();
	const { loading, sendContactInquiry } = useEmailService();
	const [formData, setFormData] = useState<ContactFormData>({
		name: '',
		email: '',
		phone: '',
		message: '',
	});

	const [submitted, setSubmitted] = useState(false);
	const [submissionError, setSubmissionError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmissionError(null);

		if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
			setSubmissionError('Please fill out all required fields.');
			return;
		}

		try {
			const result = await sendContactInquiry(formData);
			if (result.success) {
				setSubmitted(true);
				trackEvent('contact_submit', { category: 'contact_form' });
				setFormData({ name: '', email: '', phone: '', message: '', website: '' });
			} else {
				setSubmissionError(result.message);
			}
		} catch (err: any) {
			console.error('Failed to send contact inquiry:', err);
			setSubmissionError('An unexpected error occurred. Please try again.');
		}
	};

	const handleWhatsAppClick = () => {
		trackEvent('click_whatsapp', { category: 'contact_page' });
		const defaultText = encodeURIComponent(
			'Hello Elephant Expeditions! I am exploring your safaris and would love to request custom itinerary details.',
		);

		const whatsappUrl = `https://wa.me/41765456616?text=${defaultText}`;
		window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<div
			id='contact-us-page'
			className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-20 animate-fade-in space-y-12'
		>
			{/* Page Header block */}
			<div className='text-center max-w-2xl mx-auto'>
				{/* <span className='font-sans text-[10px] tracking-[0.3em] text-brand-gold-dark font-extrabold uppercase block mb-2'>
					{t('contactConcierge')}
				</span> */}
				<h2 className='font-serif text-3xl sm:text-4xl text-brand-forest font-bold tracking-tight'>
					{t('letsDesignExperience')}
				</h2>
				<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light leading-relaxed font-light mt-2'>
					{t('contactIntroText')}
				</p>
			</div>

			{/* Main Grid: Left details, Right form */}
			<div className='grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch'>
				{/* Left Column: Direct details (40% width on large screens) */}
				<div className='lg:col-span-5 bg-brand-forest text-earth-cream rounded-2xl p-8 sm:p-10 flex flex-col justify-between shadow-xl relative overflow-hidden'>
					{/* Subtle textured element */}
					<div className='absolute top-0 right-0 w-44 h-44 bg-white/5 rounded-full blur-2xl pointer-events-none' />

					<div className='space-y-10'>
						<div>
							{/* <span className='font-sans text-[9px] tracking-[0.2em] text-brand-gold uppercase font-bold block mb-3'>
								{t('conciergeDesks')}
							</span> */}
							<h3 className='font-serif text-2xl text-white font-medium'>Elephant Expeditions</h3>
							<p className='font-sans text-xs text-earth-sand-light/80 mt-2 leading-relaxed font-light'>
								{t('conciergeDesksText')}
							</p>
						</div>

						{/* Structured Contact Rows */}
						<div className='space-y-6'>
							{/* Row 1: Location */}
							<div className='flex items-start space-x-4'>
								<div className='w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-brand-gold shrink-0 mt-0.5'>
									<MapPin className='w-5 h-5' />
								</div>
								<div>
									<h4 className='font-serif text-sm font-semibold text-white'>{t('ourLocations')}</h4>
									<p className='font-sans text-xs text-earth-sand-light/90 font-light mt-0.5'>
										{t('switzerlandAndSouthAfrica')}
									</p>
								</div>
							</div>

							{/* Row 2: Phone */}
							<div className='flex items-start space-x-4'>
								<div className='w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-brand-gold shrink-0 mt-0.5'>
									<Phone className='w-5 h-5' />
								</div>
								<div>
									<h4 className='font-serif text-sm font-semibold text-white'>{t('directLine')}</h4>
									<a
										href='tel:+41792686633'
										className='font-sans text-xs text-earth-sand-light/90 font-light mt-0.5 block hover:text-brand-gold transition-colors'
									>
										+41 79 268 66 33
									</a>
								</div>
							</div>

							{/* Row 3: WhatsApp Support */}
							<div className='flex items-start space-x-4'>
								<div className='w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-brand-gold shrink-0 mt-0.5'>
									<MessageSquare className='w-5 h-5' />
								</div>
								<div>
									<h4 className='font-serif text-sm font-semibold text-white'>{t('instantWhatsAppChat')}</h4>
									<p className='font-sans text-[11px] text-earth-sand-light/70 font-light mt-0.5'>
										{t('connectWhatsAppText')}
									</p>
									<button
										onClick={handleWhatsAppClick}
										className='mt-2 inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 font-sans text-[11px] font-semibold text-white uppercase tracking-wider transition-all duration-200 cursor-pointer'
									>
										<span>{t('openWhatsAppButton')}</span>
									</button>
								</div>
							</div>

							{/* Row 4: Email */}
							<div className='flex items-start space-x-4'>
								<div className='w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-brand-gold shrink-0 mt-0.5'>
									<Mail className='w-5 h-5' />
								</div>
								<div>
									<h4 className='font-serif text-sm font-semibold text-white'>{t('officialCorrespondence')}</h4>
									<a
										href='mailto:info@elephant-expeditions.com'
										className='font-sans text-xs text-earth-sand-light/90 font-light mt-0.5 block hover:text-brand-gold underline transition-colors'
									>
										info@elephant-expeditions.com
									</a>
								</div>
							</div>

							{/* Socials Block */}
							<div className='pt-4 border-t border-white/5 flex flex-col space-y-2.5'>
								<h4 className='font-sans text-[10px] tracking-widest text-brand-gold uppercase font-bold'>
									{t('followOurJourneys')}
								</h4>
								<div className='flex items-center space-x-3'>
									<a
										href='https://www.instagram.com/elephant_expeditions/profilecard/'
										target='_blank'
										rel='noopener noreferrer'
										className='flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-earth-sand-light hover:text-brand-gold hover:bg-white/10 hover:border-brand-gold/45 transition-all duration-200'
										title='Instagram Profile'
									>
										<InstagramIcon className='w-4 h-4 text-brand-gold' />
										<span className='font-sans text-[11px] font-medium'>Instagram</span>
									</a>
									<a
										href='https://www.facebook.com/people/Elephant-Expeditions/61571970292947/'
										target='_blank'
										rel='noopener noreferrer'
										className='flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-earth-sand-light hover:text-brand-gold hover:bg-white/10 hover:border-brand-gold/45 transition-all duration-200'
										title='Facebook Page'
									>
										<FacebookIcon className='w-4 h-4 text-brand-gold' />
										<span className='font-sans text-[11px] font-medium'>Facebook</span>
									</a>
								</div>
							</div>
						</div>
					</div>

					{/* Bottom badge */}
					<div className='mt-8 pt-8 border-t border-white/10 flex items-center space-x-2'>
						<Sparkles className='w-4 h-4 text-brand-gold' />
						<span className='font-sans text-[10px] text-brand-gold uppercase tracking-widest font-bold'>
							{t('guaranteedResponseText')}
						</span>
					</div>
				</div>

				{/* Right Column: Interaction form (70% width on large screens) */}
				<div
					id='contact-form-container'
					className='lg:col-span-7 bg-white rounded-2xl border border-earth-sand/35 shadow-sm p-8 sm:p-10 flex flex-col justify-between'
				>
					{submitted ? (
						<div className='my-auto text-center space-y-6 py-12 animate-fade-in max-w-md mx-auto'>
							<div className='w-16 h-16 bg-brand-forest/10 text-brand-forest rounded-full flex items-center justify-center mx-auto shadow-sm'>
								<CheckCircle2 className='w-10 h-10' />
							</div>
							<div className='space-y-2'>
								<h3 className='font-serif text-2xl text-brand-forest font-bold'>{t('inquiryForwarded')}</h3>
								<p className='font-sans text-xs sm:text-sm text-brand-charcoal-light leading-relaxed font-light'>
									{t('inquiryForwardedText')}
								</p>
							</div>
							<button
								onClick={() => setSubmitted(false)}
								className='mt-4 px-6 py-2.5 rounded-lg border border-brand-forest text-brand-forest font-sans text-xs font-semibold uppercase tracking-widest hover:bg-brand-forest hover:text-white transition-colors duration-200 cursor-pointer'
							>
								{t('sendAnotherMessage')}
							</button>
						</div>
					) : (
						<form onSubmit={handleSubmit} className='space-y-6'>
							<div className='border-b border-earth-sand/30 pb-4'>
								<h3 className='font-serif text-lg text-brand-forest font-semibold'>{t('conciergeMessageStream')}</h3>
								<p className='font-sans text-[11px] text-brand-charcoal-light font-light mt-0.5'>
									{t('conciergeMessageStreamText')}
								</p>
							</div>

							{/* Honeypot field for anti-spam protection */}
							<div style={{ display: 'none' }} aria-hidden='true'>
								<input
									type='text'
									name='website'
									value={formData.website || ''}
									onChange={e => setFormData({ ...formData, website: e.target.value })}
									tabIndex={-1}
									autoComplete='off'
								/>
							</div>

							{submissionError && (
								<div className='p-3.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs font-sans flex items-center space-x-2 animate-pulse'>
									<AlertCircle className='w-4.5 h-4.5 shrink-0' />
									<span>{submissionError}</span>
								</div>
							)}

							{/* Grid 2x2 for Inputs */}
							<div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
								{/* Name */}
								<div className='space-y-1.5 text-left'>
									<label
										htmlFor='contact-name'
										className='font-sans text-[11px] font-bold text-brand-forest uppercase tracking-wider block'
									>
										{t('fullName')} <span className='text-brand-terracotta'>*</span>
									</label>
									<input
										type='text'
										id='contact-name'
										required
										placeholder='e.g. Eleanor Vance'
										value={formData.name}
										onChange={e => setFormData({ ...formData, name: e.target.value })}
										className='w-full bg-earth-cream/40 border border-earth-sand/50 rounded-lg px-3.5 py-2.5 text-xs font-sans placeholder-brand-charcoal-light/30 focus:outline-none focus:border-brand-forest focus:bg-white transition-all duration-200 text-brand-forest'
									/>
								</div>

								{/* Email */}
								<div className='space-y-1.5 text-left'>
									<label
										htmlFor='contact-email'
										className='font-sans text-[11px] font-bold text-brand-forest uppercase tracking-wider block'
									>
										{t('emailAddress')} <span className='text-brand-terracotta'>*</span>
									</label>
									<input
										type='email'
										id='contact-email'
										required
										placeholder='e.g. eleanor@vancepatron.com'
										value={formData.email}
										onChange={e => setFormData({ ...formData, email: e.target.value })}
										className='w-full bg-earth-cream/40 border border-earth-sand/50 rounded-lg px-3.5 py-2.5 text-xs font-sans placeholder-brand-charcoal-light/30 focus:outline-none focus:border-brand-forest focus:bg-white transition-all duration-200 text-brand-forest'
									/>
								</div>

								{/* Phone Line (Optional but prompt requests it as optional details) */}
								<div className='sm:col-span-2 space-y-1.5 text-left'>
									<label
										htmlFor='contact-phone'
										className='font-sans text-[11px] font-bold text-brand-forest uppercase tracking-wider block'
									>
										{t('contactPhoneLine')}{' '}
										<span className='text-brand-charcoal-light/30 font-light'>{t('optionalLabel')}</span>
									</label>
									<input
										type='tel'
										id='contact-phone'
										placeholder='e.g. +41765456616'
										autoComplete='mobile tel'
										value={formData.phone}
										onChange={e => setFormData({ ...formData, phone: e.target.value })}
										className='w-full bg-earth-cream/40 border border-earth-sand/50 rounded-lg px-3.5 py-2.5 text-xs font-sans placeholder-brand-charcoal-light/30 focus:outline-none focus:border-brand-forest focus:bg-white transition-all duration-200 text-brand-forest'
									/>
								</div>

								{/* Message */}
								<div className='sm:col-span-2 space-y-1.5 text-left'>
									<label
										htmlFor='contact-message'
										className='font-sans text-[11px] font-bold text-brand-forest uppercase tracking-wider block'
									>
										{t('detailedInquiryMessage')} <span className='text-brand-terracotta'>*</span>
									</label>
									<textarea
										id='contact-message'
										required
										rows={5}
										placeholder='Describe your destination goals, expected duration, group dynamics, or outline custom routes you would love to discover...'
										value={formData.message}
										onChange={e => setFormData({ ...formData, message: e.target.value })}
										className='w-full bg-earth-cream/40 border border-earth-sand/50 rounded-lg px-3.5 py-2.5 text-xs font-sans placeholder-brand-charcoal-light/30 focus:outline-none focus:border-brand-forest focus:bg-white transition-all duration-200 text-brand-forest leading-relaxed'
									/>
								</div>
							</div>

							{/* Submit CTA button */}
							<button
								type='submit'
								disabled={loading}
								className={`w-full py-3.5 rounded-lg font-sans text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 disabled:opacity-50 cursor-pointer flex items-center justify-center space-x-2 ${
									loading
										? 'bg-brand-charcoal-light'
										: 'bg-brand-forest hover:bg-brand-forest-light border border-brand-forest shadow-md'
								}`}
							>
								{loading ? (
									<>
										<svg className='animate-spin -ml-1 mr-3 h-4 w-4 text-white' fill='none' viewBox='0 0 24 24'>
											<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
											<path
												className='opacity-75'
												fill='currentColor'
												d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
											/>
										</svg>
										<span>{t('transmittingCorrespondence')}</span>
									</>
								) : (
									<>
										<Send className='w-3.5 h-3.5' />
										<span>{t('transmitMessage')}</span>
									</>
								)}
							</button>
						</form>
					)}
				</div>
			</div>
		</div>
	);
}
